import React, { useEffect, useState, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Heart, CheckCircle2, Loader2 } from 'lucide-react';

const BillingSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [status, setStatus] = useState('checking');
  const [planId, setPlanId] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) pollStatus(0);
  }, [sessionId]);

  const pollStatus = async (attempt) => {
    if (attempt >= 5) { setStatus('timeout'); return; }
    try {
      const res = await axios.get(`${API}/billing/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        setPlanId(res.data.plan_id);
      } else if (res.data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => pollStatus(attempt + 1), 2000);
      }
    } catch {
      setTimeout(() => pollStatus(attempt + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5" data-testid="billing-success">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="relative w-20 h-20 flex items-center justify-center mx-auto">
          <div className="absolute inset-0 rounded-full border-2 border-cyan-300/40" />
          <div className="absolute inset-3 rounded-full border-2 border-cyan-400/60" />
          <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
            <Heart className="w-5 h-5 text-white" fill="currentColor" />
          </div>
        </div>

        {status === 'checking' && (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-cyan-500 mx-auto" />
            <h2 className="text-xl font-bold text-foreground">Processing your payment...</h2>
            <p className="text-muted-foreground">Just a moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <h2 className="text-2xl font-bold text-foreground">You're all set!</h2>
            <p className="text-muted-foreground">
              {planId === 'premium' ? "Welcome to Coach Supported! Shannon is ready to support you." : "Welcome to Basic! Your care circle is ready."}
            </p>
            <Button className="btn-primary w-full max-w-xs mx-auto" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </>
        )}

        {status === 'timeout' && (
          <>
            <h2 className="text-xl font-bold text-foreground">Still processing</h2>
            <p className="text-muted-foreground">Your payment may still be processing. Check back in a moment.</p>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
          </>
        )}

        {status === 'expired' && (
          <>
            <h2 className="text-xl font-bold text-foreground">Session expired</h2>
            <p className="text-muted-foreground">This payment session has expired. Please try again.</p>
            <Button className="btn-primary" onClick={() => navigate('/billing')}>Try Again</Button>
          </>
        )}
      </div>
    </div>
  );
};

export default BillingSuccess;
